import { create } from 'zustand'
import { friendlyMessage } from '@/api/http'
import { projectApi, type CreateProjectInput, type PatchProjectInput } from '@/api/projectApi'
import type { Modalities, Project } from '@/types/nf'

interface ProjectState {
  projects: Project[]
  current: Project | null
  loading: boolean
  error: string | null
  load: (userId: string) => Promise<void>
  create: (input: CreateProjectInput) => Promise<Project | null>
  select: (id: string) => Promise<Project | null>
  update: (id: string, patch: PatchProjectInput) => Promise<Project | null>
  setModalities: (id: string, mods: Modalities) => Promise<Modalities | null>
}

function getErrorMessage(err: unknown) {
  return friendlyMessage(err)
}

let latestSelectRequest = 0

export const useProjectStore = create<ProjectState>((set) => ({
  projects: [],
  current: null,
  loading: false,
  error: null,

  load: async (userId) => {
    set({ loading: true, error: null })
    try {
      const projects = await projectApi.list(userId)
      set({ projects, loading: false })
    } catch (err) {
      set({ error: getErrorMessage(err), loading: false })
    }
  },

  create: async (input) => {
    set({ loading: true, error: null })
    try {
      const project = await projectApi.create(input)
      set((state) => ({ projects: [project, ...state.projects], current: project, loading: false }))
      return project
    } catch (err) {
      set({ error: getErrorMessage(err), loading: false })
      return null
    }
  },

  select: async (id) => {
    const requestId = ++latestSelectRequest
    set({ loading: true, error: null })
    try {
      const project = await projectApi.get(id)
      if (requestId === latestSelectRequest) {
        set({ current: project, loading: false })
      }
      return project
    } catch (err) {
      if (requestId === latestSelectRequest) {
        set({ error: getErrorMessage(err), loading: false })
      }
      return null
    }
  },

  update: async (id, patch) => {
    set({ loading: true, error: null })
    try {
      const project = await projectApi.patch(id, patch)
      set((state) => ({
        current: state.current?.id === id ? project : state.current,
        projects: state.projects.map((item) => (item.id === id ? project : item)),
        loading: false,
      }))
      return project
    } catch (err) {
      set({ error: getErrorMessage(err), loading: false })
      return null
    }
  },

  setModalities: async (id, mods) => {
    set({ error: null })
    try {
      return await projectApi.putModalities(id, mods)
    } catch (err) {
      set({ error: getErrorMessage(err) })
      return null
    }
  },
}))
